import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Package, Upload, FileArchive, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [files, setFiles] = useState([]);
  const [company, setCompany] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = () => {
    api.get('/batches').then(res => setBatches(res.data));
    api.get('/auth/me').then(me => api.get(`/companies/${me.data.company_id}`)).then(res => setCompany(res.data));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('file-upload');
    const file = fileInput.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/batches/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Batch uploaded successfully');
      setOpen(false);
      loadBatches();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const viewBatchFiles = async (batchId) => {
    const res = await api.get(`/batches/${batchId}/files`);
    setFiles(res.data);
    setSelectedBatch(batchId);
  };

  const confirmDelete = (target) => {
    setDeleteTarget(target);
    setDeleteDialogOpen(true);
  };

  const deleteBatch = async (batchId) => {
    try {
      await api.delete(`/batches/${batchId}`);
      toast.success('Batch deleted');
      setSelectedBatch(null);
      setFiles([]);
      loadBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted');
      await viewBatchFiles(selectedBatch);
      loadBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'batch') {
      await deleteBatch(deleteTarget.id);
    } else {
      await deleteFile(deleteTarget.id);
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (value >= 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
    if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(2)} MB`;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
    return `${value} B`;
  };

  const storagePercent = company ? Math.min(100, ((company.used_storage_bytes || 0) / Math.max(company.storage_limit_bytes || 1, 1)) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="batches-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>File Batches</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-batch-button">
              <Plus className="w-4 h-4 mr-2" />
              Upload Batch
            </Button>
          </DialogTrigger>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Upload File Batch</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label>Select ZIP or single file</Label>
                <Input id="file-upload" type="file" accept=".zip,.pdf,.docx,.xlsx,.txt" data-testid="file-input" />
              </div>
              <Button type="submit" className="w-full" disabled={uploading} data-testid="submit-upload-button">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTarget?.type === 'batch' ? 'Delete File Batch' : 'Delete File'}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'batch'
                ? 'Delete this file batch and all its files? This cannot be undone.'
                : `Delete ${deleteTarget?.name || 'this file'}? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {company && (
        <Card className="glass-card">
          <CardContent className="pt-5">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Company storage</span>
              <span>{storagePercent.toFixed(1)}% · {formatBytes(company.used_storage_bytes)} / {formatBytes(company.storage_limit_bytes)}</span>
            </div>
            <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full bg-blue-600" style={{ width: `${storagePercent}%` }} />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {batches.map(batch => (
          <Card key={batch.id} className="glass-card cursor-pointer hover:shadow-xl transition-shadow" onClick={() => viewBatchFiles(batch.id)} data-testid={`batch-card-${batch.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  {batch.original_zip_name ? <FileArchive className="w-5 h-5" /> : <Package className="w-5 h-5" />}
                  {batch.original_zip_name || 'Single File'}
                </CardTitle>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); confirmDelete({ type: 'batch', id: batch.id }); }} data-testid={`delete-batch-${batch.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Files:</span>
                  <span className="font-medium">{batch.file_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Size:</span>
                  <span className="font-medium">{formatBytes(batch.total_size_bytes)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Uploaded:</span>
                  <span className="font-medium">{new Date(batch.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedBatch && files.length > 0 && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Batch Files ({files.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {files.map(file => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`file-item-${file.id}`}>
                  <div>
                    <div className="font-medium">{file.original_filename}</div>
                    <div className="text-sm text-gray-500">{(file.size_bytes / 1024).toFixed(2)} KB</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500">{file.mime_type}</div>
                    <Button size="sm" variant="outline" onClick={() => confirmDelete({ type: 'file', id: file.id, name: file.original_filename })} data-testid={`delete-file-${file.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
